import { DisposableEventAggregator } from '@repo/rpc/disposable-event-aggregator';
import type { MessageData } from '@repo/rpc/rpc';
import type { IDisposable } from '@repo/types/shared/disposable';
import type { VoiceState } from '@repo/types/voice';
import { createLogger } from '@repo/utility/logging';
import type { ClientRpc } from './rpc-types';

const logger = createLogger(process.env.NODE_ENV ?? 'production');

export class VoiceClient implements IDisposable {
    private events = new DisposableEventAggregator();
    private rpc: ClientRpc;
    private readonly handlerActions: string[] = [];

    public constructor(rpc: ClientRpc) {
        this.rpc = rpc;

        this.handle('voice_start', async () => this.events.publish('voice_start'));
        this.handle('voice_stop', async () => this.events.publish('voice_stop'));
    }

    private handle(action: string, handler: (data: never) => MessageData | Promise<MessageData>): void {
        this.handlerActions.push(action);
        this.rpc.setHandler(action, handler);
    }

    public onVoiceStart(handler: () => void): IDisposable {
        return this.events.subscribe('voice_start', handler);
    }

    public onVoiceStop(handler: () => void): IDisposable {
        return this.events.subscribe('voice_stop', handler);
    }

    public async notifyVoiceStateChange(state: VoiceState): Promise<void> {
        await this.rpc.execute('voice_state_change', { state });
    }

    public async notifyVoiceAnswerStart(question: string): Promise<void> {
        await this.rpc.execute('voice_answer_start', { question });
    }

    public notifyVoiceAnswerToken(token: string): void {
        this.rpc.execute('voice_answer_token', { token }).catch(err => {
            logger.warn('voice_answer_token RPC failed', { error: err });
        });
    }

    public notifyVoiceAudioLevel(level: number): void {
        this.rpc.execute('voice_audio_level', { level }).catch(err => {
            logger.warn('voice_audio_level RPC failed', { error: err });
        });
    }

    public notifyVoiceAnswerClear(): void {
        this.rpc.execute('voice_answer_clear', null).catch(err => {
            logger.warn('voice_answer_clear RPC failed', { error: err });
        });
    }

    public async notifyVoiceAnswerDone(): Promise<void> {
        await this.rpc.execute('voice_answer_done', null);
    }

    public async notifyVoiceError(message: string): Promise<void> {
        await this.rpc.execute('voice_error', { message });
    }

    public dispose(): void {
        for (const action of this.handlerActions) this.rpc.removeHandler(action);
        this.events.dispose();
    }
}
